import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Compass, Sparkles, Heart, BookOpen, Users } from 'lucide-react';
import vission from '../../assets/images/vis.jpg';
import mission from '../../assets/images/mission.jpg';

export default function VisionMission() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="vj-vision-mission-page py-5">
      <Helmet>
        <title>Vision & Mission - Vidyajyoti College of Theology</title>
        <meta name="description" content="Vision and Mission of Vidyajyoti College of Theology: Integral theological formation for men and women dedicated to the service of others." />
      </Helmet>

      <div className="container">
        {/* Header */}
        <div className="text-center mx-auto pb-5" style={{ maxWidth: '750px' }}>
          <span className="text-burgundy fw-bold text-uppercase small" style={{ letterSpacing: '0.1em' }}>
            FOUNDATIONAL VALUES
          </span>
          <h1 className="vj-card-title display-6 fw-bold mt-1 text-dark-main">
            Vision & Mission
          </h1>
          <p className="text-muted mt-2">
            Forming contemplative leaders and contextual theologians dedicated to faith, justice, and solidarity.
          </p>
          <div className="mx-auto mt-2" style={{ width: '60px', height: '3px', background: '#79031d', borderRadius: '2px' }} />
        </div>

        {/* Section 1: Vision */}
        <div className="row g-4 align-items-stretch mb-5">
          <div className="col-lg-6">
            <div className="vj-modern-card p-4 p-md-5 h-100 d-flex flex-column justify-content-center" style={{ borderLeft: '5px solid #1a2b56' }}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge">
                  <Compass size={22} />
                </div>
                <h2 className="fw-bold fs-3 text-dark mb-0">Our Vision</h2>
              </div>
              <p className="text-muted lead fs-6" style={{ lineHeight: '1.8' }}>
                Vidyajyoti envisions an <strong>integral formation</strong> which encompasses the human, intellectual, spiritual, and contextual-pastoral dimensions of our students.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8' }}>
                The training empowers learners to become <em>“men and women for and with others”</em>, who selflessly serve society—especially the marginalized and vulnerable—irrespective of caste, colour, or creed.
              </p>
            </div>
          </div>
          <div className="col-lg-6 text-center">
            <div 
              className="vj-modern-card shadow-sm p-3 p-md-4 h-100 d-flex align-items-center justify-content-center bg-white"
            >
              <img 
                src={vission} 
                alt="Vidyajyoti Vision" 
                className="img-fluid rounded-3" 
                style={{ 
                  maxHeight: '440px', 
                  maxWidth: '100%', 
                  width: 'auto', 
                  height: 'auto', 
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto'
                }} 
              />
            </div>
          </div>
        </div>

        {/* Section 2: Mission */}
        <div className="row g-4 align-items-stretch mb-5 flex-lg-row-reverse">
          <div className="col-lg-6">
            <div className="vj-modern-card p-4 p-md-5 h-100 d-flex flex-column justify-content-center" style={{ borderLeft: '5px solid #79031d' }}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="vj-icon-badge burgundy">
                  <Sparkles size={22} />
                </div>
                <h2 className="fw-bold fs-3 text-dark mb-0">Our Mission</h2>
              </div>
              <p className="text-muted" style={{ lineHeight: '1.8' }}>
                “Initiatives and activities of Vidyajyoti are intended to promote the values of Christian tradition from the living experiences of the Context. In particular, we champion the dignity of the human person, the protection of creation, ecumenical dialogue, interreligious understanding, and reconciliation.”
              </p>
              <span className="text-burgundy fw-bold small">— Statutes of Vidyajyoti, Art. 1</span>
            </div>
          </div>
          <div className="col-lg-6 text-center">
            <div 
              className="vj-modern-card shadow-sm p-3 p-md-4 h-100 d-flex align-items-center justify-content-center bg-white"
            >
              <img 
                src={mission} 
                alt="Vidyajyoti Mission" 
                className="img-fluid rounded-3" 
                style={{ 
                  maxHeight: '440px', 
                  maxWidth: '100%', 
                  width: 'auto', 
                  height: 'auto', 
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto'
                }} 
              />
            </div>
          </div>
        </div>

        {/* Section 3: Four Pillars of Formation */}
        <div className="pt-3">
          <h3 className="fw-bold fs-4 text-dark-main text-center mb-4">Four Pillars of Integral Formation</h3>
          <div className="row g-4">
            <div className="col-md-3 col-sm-6 d-flex">
              <div className="vj-modern-card p-4 text-center w-100">
                <div className="vj-icon-badge mx-auto mb-3">
                  <Heart size={20} />
                </div>
                <h5 className="fw-bold fs-6 text-dark mb-2">Human Formation</h5>
                <p className="text-muted small mb-0">Cultivating emotional maturity, psychological resilience, and ethical integrity.</p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 d-flex">
              <div className="vj-modern-card p-4 text-center w-100">
                <div className="vj-icon-badge burgundy mx-auto mb-3">
                  <BookOpen size={20} />
                </div>
                <h5 className="fw-bold fs-6 text-dark mb-2">Intellectual Growth</h5>
                <p className="text-muted small mb-0">Rigorous academic research and critical reflection on contemporary challenges.</p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 d-flex">
              <div className="vj-modern-card p-4 text-center w-100">
                <div className="vj-icon-badge mx-auto mb-3">
                  <Sparkles size={20} />
                </div>
                <h5 className="fw-bold fs-6 text-dark mb-2">Spiritual Depth</h5>
                <p className="text-muted small mb-0">Ignatian discernment, contemplative prayer, and finding God in all things.</p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 d-flex">
              <div className="vj-modern-card p-4 text-center w-100">
                <div className="vj-icon-badge burgundy mx-auto mb-3">
                  <Users size={20} />
                </div>
                <h5 className="fw-bold fs-6 text-dark mb-2">Pastoral Praxis</h5>
                <p className="text-muted small mb-0">Direct engagement with slums, hospital patients, prisons, and marginalized groups.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
